<template>
  <container>
    <modal-one-signal :race-id="raceIdToNotification" @close-modal="closeOneSignal" v-if="raceIdToNotification" />
    <div class="pg-title-row">
      <div>
        <h1 class="pg-title">Races</h1>
        <p class="pg-title-sub">Create, review and manage scheduled LiveRun race events</p>
      </div>
      <router-link to="/admin/races/add" class="rl-primary-btn" id="newrace">
        <svg class="rl-btn-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 5v14M5 12h14"/>
        </svg>
        New Race
      </router-link>
    </div>

    <section class="rl-card">
      <div class="rl-card-header">
        <div class="rl-header-main">
          <span class="rl-icon-pill">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M8 6h13"/>
              <path d="M8 12h13"/>
              <path d="M8 18h13"/>
              <path d="M3 6h.01"/>
              <path d="M3 12h.01"/>
              <path d="M3 18h.01"/>
            </svg>
          </span>
          <div>
            <h2 class="rl-card-title">Race Schedule</h2>
            <p class="rl-card-subtitle">Track upcoming, active and completed races from one operational list</p>
          </div>
        </div>
        <span class="rl-count-badge">{{ totalElements }} races</span>
      </div>

      <div class="rl-table-wrap">
        <table class="rl-table">
          <thead class="rl-table-head">
            <tr class="rl-table-row">
              <th class="rl-th rl-th--name">Name</th>
              <th class="rl-th rl-th--description">Description</th>
              <th class="rl-th rl-th--duration">Duration</th>
              <th class="rl-th rl-th--status">Status</th>
              <th class="rl-th rl-th--type">Type</th>
              <th class="rl-th rl-th--date">Date</th>
              <th class="rl-th rl-th--actions">Actions</th>
            </tr>
          </thead>
          <tbody class="rl-table-body">
            <template v-if="racesLoading">
              <tr v-for="n in 5" :key="'race-skeleton-' + n" class="rl-table-row rl-table-row--skeleton">
                <td class="rl-td"><div class="rl-sk-line rl-sk-line--name sk-shimmer"></div></td>
                <td class="rl-td"><div class="rl-sk-line rl-sk-line--wide sk-shimmer"></div></td>
                <td class="rl-td"><div class="rl-sk-line rl-sk-line--short sk-shimmer"></div></td>
                <td class="rl-td"><div class="rl-sk-pill sk-shimmer"></div></td>
                <td class="rl-td"><div class="rl-sk-line rl-sk-line--medium sk-shimmer"></div></td>
                <td class="rl-td"><div class="rl-sk-line rl-sk-line--date sk-shimmer"></div></td>
                <td class="rl-td rl-td--actions"><div class="rl-sk-actions sk-shimmer"></div></td>
              </tr>
            </template>

            <template v-else-if="raceRows.length">
              <tr v-for="race in raceRows" :key="race.id" class="rl-table-row">
                <td class="rl-td rl-td--name">
                  <span class="rl-race-name">{{ race.name }}</span>
                </td>
                <td class="rl-td rl-td--description">
                  <span class="rl-muted-text">{{ race.description || 'No description' }}</span>
                </td>
                <td class="rl-td rl-td--duration">{{ race.duration }} minutes</td>
                <td class="rl-td rl-td--status">
                  <span class="rl-status-pill" :class="'rl-status-pill--' + getStatusClass(race)">
                    {{ getStatusRace(race) }}
                  </span>
                </td>
                <td class="rl-td rl-td--type">{{ race.type }}</td>
                <td class="rl-td rl-td--date">{{ race.date | date }}</td>
                <td class="rl-td rl-td--actions">
                  <div class="rl-row-actions" v-show="activeRaceId !== race.id || activeRaceId === 0">
                    <router-link :to="{ path:`/admin/races/edit/${race.id}` }" class="rl-ghost-btn" v-if="race.startDate === null">
                      <svg class="rl-btn-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                      Edit
                    </router-link>
                    <button v-show="activeRaceId !== race.id" class="rl-danger-btn" type="button" v-on:click="handleDelete(race)">
                      <svg class="rl-btn-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M3 6h18"/>
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        <path d="M10 11v6M14 11v6"/>
                      </svg>
                      Delete
                    </button>
                  </div>

                  <div class="rl-confirmation" v-show="deleteMode && activeRaceId === race.id">
                    <span class="rl-confirmation-text">Delete?</span>
                    <button class="rl-confirm-btn" type="button" v-on:click="deleteRaceSelected(race, $event)" :disabled="deletingRaceId === race.id">
                      {{ deletingRaceId === race.id ? 'Deleting...' : 'Yes' }}
                    </button>
                    <button v-on:click="Clear(race)" class="rl-cancel-btn" type="button" :disabled="deletingRaceId === race.id">No</button>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <div class="rl-empty" v-if="!racesLoading && !raceRows.length">
          <svg class="rl-empty-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M8 6h13"/>
            <path d="M8 12h13"/>
            <path d="M8 18h13"/>
            <path d="M3 6h.01"/>
            <path d="M3 12h.01"/>
            <path d="M3 18h.01"/>
          </svg>
          <p class="rl-empty-title">No races scheduled</p>
          <p class="rl-empty-subtitle">Create a new race to begin building the LiveRun schedule.</p>
        </div>
      </div>

      <div v-if="!racesLoading && totalElements > 0" class="rl-pagination">
        <span class="rl-page-total">{{ totalElements }} total</span>
        <button class="rl-page-btn rl-page-btn--icon" type="button" :disabled="pageNo === 1" @click="pageChange(pageNo - 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
        <button
          v-for="page in visiblePageNumbers"
          :key="'page-' + page"
          class="rl-page-btn"
          type="button"
          :class="{ 'rl-page-btn--active': pageNo === page }"
          @click="pageChange(page)"
        >
          {{ page }}
        </button>
        <button class="rl-page-btn rl-page-btn--icon" type="button" :disabled="pageNo >= totalPages" @click="pageChange(pageNo + 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>
        <div class="rl-page-size-wrap">
          <select class="rl-page-size" :value="pageSize" @change="pageSizeChange(Number($event.target.value))">
            <option :value="10">10 / page</option>
            <option :value="25">25 / page</option>
            <option :value="50">50 / page</option>
            <option :value="100">100 / page</option>
          </select>
          <span class="rl-page-size-chevron">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </span>
        </div>
      </div>
    </section>

    <!-- Debug Options Block -->
    <debug-race-creator v-if="showDebugOptions"></debug-race-creator>

  </container>
</template>

<script>
  import {
    mapGetters,
    mapActions
  } from 'vuex'
  import Container from '@/components/Container'
  import ModalOneSignal from '@/components/ModalOneSignal'
  import DebugRaceCreator from '@/components/Races/DebugRaceCreator'
  import racesByTrainerService from '@/services/racesByTrainer'
  export default {
    name: 'list-race',
    data: () => ({
      deleteMode: false,
      activeRaceId: 0,
      delete: true,
      racesLoading: true,
      deletingRaceId: null,
      raceIdToNotification: null,
      showQuestionModal: false,
      raceId: null,
      todayQuestion: {},
      raceRows: [],
      pageNo: 1,
      pageSize: 25,
      totalElements: 0
    }),
    props: {
      isSaved: Boolean
    },
    computed: {
      ...mapGetters({
        trainer: 'Trainer/trainer'
      }),
      trainerId() {
        return this.trainer && this.trainer.id ? this.trainer.id : null
      },
      totalPages() {
        return Math.max(Math.ceil((this.totalElements || 0) / this.pageSize), 1)
      },
      visiblePageNumbers() {
        const total = this.totalPages
        if (total <= 0) {
          return []
        }

        const range = 7
        let start = Math.max(this.pageNo - Math.floor(range / 2), 1)
        let end = Math.min(start + range - 1, total)
        start = Math.max(end - range + 1, 1)

        const pages = []
        for (let page = start; page <= end; page += 1) {
          pages.push(page)
        }
        return pages
      },
      showDebugOptions() {
        return this.$isDevMode()
      }
    },
    methods: {
      closeOneSignal() {
        this.raceIdToNotification = null
      },
      initialize() {
        try {
          this.racesLoading = true
          this.addTaskToQueue('getTrainer')

          this.getTrainer(this.$auth.user().id)
            .then(() => {
              this.removeTaskFromQueue('getTrainer')
            })
            .catch(error => {
              console.error('Error getting trainer:', error)
              this.removeTaskFromQueue('getTrainer')
              this.racesLoading = false
            })
        } catch (error) {
          console.error('Error in initialize:', error)
          this.racesLoading = false
        }
      },
      fetchRaces() {
        if (!this.trainerId) {
          this.racesLoading = false
          return Promise.resolve()
        }

        this.racesLoading = true
        this.addTaskToQueue('fetchScheduleRaces')

        return racesByTrainerService.getAllRuns({
          trainerId: this.trainerId,
          pageNo: this.pageNo,
          pageSize: this.pageSize,
          filter: 'future'
        })
          .then((response) => {
            const payload = (response && response.body) || {}
            this.totalElements = payload.totalElements || 0
            this.raceRows = payload.content || []
          })
          .catch(error => {
            console.error('Error fetching schedule races:', error)
            this.raceRows = []
            this.totalElements = 0
          })
          .finally(() => {
            this.racesLoading = false
            this.removeTaskFromQueue('fetchScheduleRaces')
          })
      },
      pageChange(pageNo) {
        const nextPage = Math.min(Math.max(Number(pageNo) || 1, 1), this.totalPages)
        if (nextPage === this.pageNo) {
          return
        }
        this.pageNo = nextPage
        this.fetchRaces()
      },
      pageSizeChange(pageSize) {
        this.pageNo = 1
        this.pageSize = pageSize
        this.fetchRaces()
      },
      reloadCurrentPage() {
        return this.fetchRaces().then(() => {
          if (!this.raceRows.length && this.pageNo > 1) {
            this.pageNo -= 1
            return this.fetchRaces()
          }
        })
      },

      Clear(race) {
        this.activeRaceId = race.id
        this.deleteMode = false

      },
      handleDelete(race) {
        this.activeRaceId = race.id
        this.deleteMode = true

        //this.delete2[race.id] = true
        //delete2[race.id].removeAttribute('disabled', true)
        //var retVal = confirm("Are you sure you want to delete ?")
        // if( retVal == true ){
        //     alert("User wants to delete!")
        //     this.deleteRace(race)
        //     .then(this.getRaces)
        //     .then(() => this.loadingDelete(event.target, false))
        //     .catch(() => this.loadingDelete(event.target, false))
        // }
        // else{
        //     alert ("User does not want to delete!")
        // }
      },
      deleteRaceSelected(race, event) {
        this.deletingRaceId = race.id
        this.deleteRace(race)
          .then(() => this.reloadCurrentPage())
          .then(() => this.loadingDelete(event.target, false))
          .catch(() => this.loadingDelete(event.target, false))
          .finally(() => {
            this.deletingRaceId = null
          })
        this.deleteMode = false
      },
      ...mapActions({
        getTrainer: 'Trainer/get',
        deleteRace: 'Race/deleteOne',
        addTaskToQueue: 'LoadingQueue/addTaskToQueue',
        removeTaskFromQueue: 'LoadingQueue/removeTaskFromQueue'
      }),
      loadingDelete(target, active = true) {
        const btn = target

        btn.disabled = active
      },
      getStatusRace(race) {
        if (race.startDate === null && race.finishDate === null) {
          return 'to start'
        } else if (race.startDate !== null && race.finishDate === null) {
          return 'in progress'
        } else if (race.startDate !== null && race.finishDate !== null) {
          return 'finished'
        }
      },
      getStatusClass(race) {
        const status = this.getStatusRace(race)
        return status ? status.replace(/\s+/g, '-') : 'unknown'
      },
      openNotification(raceId) {
        this.raceIdToNotification = raceId
      }
    },
    created() {
      this.initialize()
      if (this.trainerId) {
        this.fetchRaces()
      }
    },
    activated() {
      if (this.trainerId) {
        this.fetchRaces()
      }
    },
    watch: {
      trainerId(newId, oldId) {
        if (!newId) {
          this.racesLoading = false
          return
        }
        if (oldId && oldId !== newId) {
          this.pageNo = 1
        }
        this.fetchRaces()
      }
    },
    components: {
      Container,
      ModalOneSignal,
      DebugRaceCreator
    }
  }

</script>
<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

</style>
<style scoped>
.pg-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
  margin-bottom: 1.5rem;
}

.pg-title {
  font-family: 'Inter', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 0;
}

.pg-title-sub {
  font-size: 13px;
  color: #8a8a9a;
  font-weight: 400;
  margin: 5px 0 0;
}

.rl-card {
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  background: #fff;
}

.rl-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.rl-header-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.rl-icon-pill {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.rl-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}

.rl-card-subtitle {
  font-size: 12px;
  color: #8a8a9a;
  margin: 3px 0 0;
}

.rl-count-badge {
  border: 1px solid #e8e8ed;
  border-radius: 999px;
  color: #5b4fcf;
  background: #f8f7ff;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 10px;
  white-space: nowrap;
}

.rl-primary-btn,
.rl-ghost-btn,
.rl-danger-btn,
.rl-confirm-btn,
.rl-cancel-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.rl-primary-btn {
  background: #5b4fcf;
  color: #fff;
  border: none;
  height: 36px;
  padding: 0 18px;
}

.rl-primary-btn:hover,
.rl-confirm-btn:hover {
  background: #4338ca;
  color: #fff;
}

.rl-ghost-btn {
  border: 1.5px solid #5b4fcf;
  background: #fff;
  color: #5b4fcf;
  height: 33px;
  min-width: 66px;
  padding: 0 12px;
}

.rl-ghost-btn:hover {
  background: #f0effe;
  color: #4338ca;
}

.rl-danger-btn {
  border: 1px solid #f0d4d4;
  background: #fff;
  color: #c0392b;
  height: 33px;
  min-width: 78px;
  padding: 0 12px;
}

.rl-danger-btn:hover {
  background: #fff5f5;
  border-color: #e6b6b6;
}

.rl-btn-icon {
  flex: 0 0 auto;
}

.rl-table-wrap {
  width: 100%;
  overflow-x: auto;
}

.rl-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 980px;
}

.rl-table-head {
  background: #fafafa;
}

.rl-th {
  color: #8a8a9a;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0;
  text-align: left;
  padding: 11px 12px;
  border-bottom: 1px solid #e8e8ed;
}

.rl-th--name {
  width: 16%;
}

.rl-th--description {
  width: 23%;
}

.rl-th--duration,
.rl-th--status {
  width: 11%;
}

.rl-th--type {
  width: 14%;
}

.rl-th--date {
  width: 13%;
}

.rl-th--actions {
  width: 18%;
  text-align: right;
}

.rl-table-row {
  border-bottom: 1px solid #f0f0f5;
}

.rl-table-body .rl-table-row:last-child {
  border-bottom: none;
}

.rl-td {
  color: #1a1a2e;
  font-size: 13px;
  vertical-align: middle;
  padding: 14px 12px;
}

.rl-td--description,
.rl-muted-text {
  color: #686878;
}

.rl-race-name {
  font-weight: 700;
  color: #1a1a2e;
}

.rl-status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  border-radius: 999px;
  padding: 0 10px;
  font-size: 11px;
  font-weight: 700;
  text-transform: capitalize;
  white-space: nowrap;
}

.rl-status-pill--to-start {
  color: #5b4fcf;
  background: #f0effe;
}

.rl-status-pill--in-progress {
  color: #0f766e;
  background: #e6f6f4;
}

.rl-status-pill--finished {
  color: #475569;
  background: #eef2f7;
}

.rl-td--actions {
  text-align: right;
}

.rl-row-actions,
.rl-confirmation {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.rl-confirmation-text {
  color: #8a8a9a;
  font-size: 12px;
  font-weight: 600;
}

.rl-confirm-btn {
  background: #5b4fcf;
  border: none;
  color: #fff;
  height: 30px;
  padding: 0 12px;
}

.rl-cancel-btn {
  background: #fff;
  border: 1px solid #e0e0ea;
  color: #686878;
  height: 30px;
  padding: 0 12px;
}

.rl-cancel-btn:hover {
  border-color: #cfcfe0;
  color: #1a1a2e;
}

.rl-confirm-btn:disabled,
.rl-cancel-btn:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

@keyframes sk-shimmer {
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}

.sk-shimmer {
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ef 50%, #f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

.rl-sk-line {
  height: 12px;
  width: 72px;
}

.rl-sk-line--name {
  width: 118px;
}

.rl-sk-line--wide {
  width: 180px;
}

.rl-sk-line--short {
  width: 64px;
}

.rl-sk-line--medium {
  width: 126px;
}

.rl-sk-line--date {
  width: 96px;
}

.rl-sk-pill {
  height: 24px;
  width: 78px;
  border-radius: 999px;
}

.rl-sk-actions {
  height: 33px;
  width: 118px;
  margin-left: auto;
  border-radius: 8px;
}

.rl-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 210px;
  text-align: center;
  color: #8a8a9a;
  border-top: 1px solid #f0f0f5;
}

.rl-empty-icon {
  color: #c8c8d8;
  margin-bottom: 12px;
}

.rl-empty-title {
  color: #1a1a2e;
  font-size: 15px;
  font-weight: 700;
  margin: 0;
}

.rl-empty-subtitle {
  font-size: 12px;
  margin: 5px 0 0;
}

.rl-pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding-top: 1.25rem;
  color: #1a1a2e;
  font-size: 13px;
  font-weight: 600;
  flex-wrap: wrap;
}

.rl-page-total {
  color: #1a1a2e;
  margin-right: 2px;
}

.rl-page-btn {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fff;
  color: #5b4fcf;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.rl-page-btn:hover:not(:disabled) {
  border-color: #5b4fcf;
  background: #f0effe;
}

.rl-page-btn:disabled {
  color: #8a8a9a;
  background: #fafafa;
  cursor: not-allowed;
}

.rl-page-btn--active,
.rl-page-btn--active:hover {
  border-color: #5b4fcf;
  background: #5b4fcf;
  color: #fff;
}

.rl-page-btn--icon {
  color: #5b4fcf;
}

.rl-page-size-wrap {
  position: relative;
}

.rl-page-size {
  height: 34px;
  min-width: 104px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fff;
  color: #1a1a2e;
  font-size: 13px;
  font-weight: 600;
  padding: 0 32px 0 10px;
  appearance: none;
  outline: none;
}

.rl-page-size:focus {
  border-color: #5b4fcf;
}

.rl-page-size-chevron {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #5b4fcf;
  pointer-events: none;
}

@media screen and (max-width: 720px) {
  .pg-title-row,
  .rl-card-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .rl-primary-btn {
    width: 100%;
  }

  .rl-card {
    padding: 1.25rem;
  }
}
</style>



// WEBPACK FOOTER //
// List.vue?f5749d20